import * as React from 'react';
import { createIntl, createIntlCache, RawIntlProvider } from 'react-intl';
import {
  isSupportedLanguage,
  parseLocale,
} from '@showell-labs/showell-commons/lib/utils/localization';
import enMessages from './locales/en.json';
import arMessages from './locales/ar.json';
import bgMessages from './locales/bg.json';
import csMessages from './locales/cs.json';
import daMessages from './locales/da.json';
import deMessages from './locales/de.json';
import esMessages from './locales/es.json';
import fiMessages from './locales/fi.json';
import frMessages from './locales/fr.json';
import heMessages from './locales/he.json';
import huMessages from './locales/hu.json';
import itMessages from './locales/it.json';
import jaMessages from './locales/ja.json';
import nbMessages from './locales/nb.json';
import nlMessages from './locales/nl.json';
import plMessages from './locales/pl.json';
import ptMessages from './locales/pt.json';
import svMessages from './locales/sv.json';
import trMessages from './locales/tr.json';
import ukMessages from './locales/uk.json';
import zhMessages from './locales/zh.json';

type Messages = Record<string, string>;

const messagesMap: Record<string, Messages> = {
  en: enMessages,
  ar: arMessages,
  bg: bgMessages,
  cs: csMessages,
  da: daMessages,
  de: deMessages,
  es: esMessages,
  fi: fiMessages,
  fr: frMessages,
  he: heMessages,
  hu: huMessages,
  it: itMessages,
  ja: jaMessages,
  nb: nbMessages,
  nl: nlMessages,
  pl: plMessages,
  pt: ptMessages,
  sv: svMessages,
  tr: trMessages,
  uk: ukMessages,
  zh: zhMessages,
};

/**
 * Resolves a raw browser locale string (e.g. "en-US", "fi", "zh-CN") to a
 * Showell-supported 2-letter language code, falling back to "en".
 */
function resolveLocale(rawLocale: string): string {
  try {
    const language = parseLocale(rawLocale)[0];
    return isSupportedLanguage(language) ? language : 'en';
  } catch {
    return 'en';
  }
}

const cache = createIntlCache();

/**
 * Provides react-intl translations for the PowerPoint add-in.
 *
 * Locale is detected from `navigator.language` (browser/OS setting) and
 * resolved to one of the 21 Showell-supported languages. Falls back to
 * English when the browser language is unsupported or when a translation key
 * is missing from the locale file (via react-intl's `defaultMessage`).
 */
export function AddInIntlProvider({ children }: { children: React.ReactNode }) {
  const locale = React.useMemo(
    () => resolveLocale(navigator.language ?? 'en'),
    []
  );

  const intl = React.useMemo(
    () =>
      createIntl(
        {
          locale,
          messages: messagesMap[locale] ?? enMessages,
          defaultLocale: 'en',
        },
        cache
      ),
    [locale]
  );

  return <RawIntlProvider value={intl}>{children}</RawIntlProvider>;
}
